import { RHFProvider } from "@/components/forms/RHFProvider"
import { SettingsLayout } from "@/components/layouts/setting-layout/SettingsLayout"
import { Card, CardContent } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Form, FormField, FormItem, FormLabel } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group"
import { H4, Small } from "@/components/ui/typography"
import { WEEKDAYS } from "@/constants/dates"
import { useFormAutoSave } from "@/hooks/useFormAutoSave"
import { useUSHolidays } from "@/queries/useHolidays"
import { WorkingSchedule, workingScheduleSchema } from "@/state/draft/workingSchedule"
import { PlainDate } from "@/utils/date"
import { zodResolver } from "@hookform/resolvers/zod"
import React from "react"
import { useForm } from "react-hook-form"

type EditWorkScheduleScreenProps = {
	reportId: string
	value: WorkingSchedule
	onSave: (data: WorkingSchedule) => void
	onSaveLocal: (data: WorkingSchedule) => void
	reportDateRange: [PlainDate, PlainDate]
	savingInProcess: boolean
}

export const EditWorkScheduleScreen: React.FC<EditWorkScheduleScreenProps> = (props) => {
	const form = useForm<WorkingSchedule>({
		defaultValues: props.value,
		resolver: zodResolver(workingScheduleSchema),
	})

	const holidays = useUSHolidays(
		props.reportDateRange[0],
		props.reportDateRange[1],
		form.getValues("considerHolidays")
	)
	const formatTime = (time: string) => {
		const [hours, minutes] = time.split(":")
		return `${hours?.padStart(2, "0")}:${minutes?.padStart(2, "0")}`
	}

	useFormAutoSave(form.control, props.onSaveLocal, props.value)

	return (
		<RHFProvider form={form}>
			<SettingsLayout
				savingInProcess={props.savingInProcess}
				reportId={props.reportId}
				title="Work Schedule"
				activeSection="work-schedule"
				onSave={() => {
					form.handleSubmit(props.onSave)()
				}}
			>
				<div className="flex flex-col gap-8">
					<div>
						<H4>What are your working hours?</H4>
						<div className="flex space-x-4 mt-3.5">
							<FormField
								control={form.control}
								name="workingHours.0"
								render={({ field }) => (
									<FormItem className="flex-1">
										<FormLabel>Start Time</FormLabel>
										<Input
											{...field}
											type="time"
											className="w-full"
											value={formatTime(field.value)}
										/>
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="workingHours.1"
								render={({ field }) => (
									<FormItem className="flex-1">
										<FormLabel>End Time</FormLabel>
										<Input
											{...field}
											type="time"
											className="w-full"
											value={formatTime(field.value)}
										/>
									</FormItem>
								)}
							/>
						</div>
					</div>
					<section>
						<FormField
							control={form.control}
							name="workingWeekDays"
							render={({ field }) => (
								<FormItem className="flex flex-col items-start">
									<H4>Select the days you typically work:</H4>
									<ToggleGroup
										type="multiple"
										value={field.value}
										onValueChange={field.onChange}
									>
										{WEEKDAYS.map((day) => (
											<ToggleGroupItem
												key={day}
												value={day}
												aria-label={day}
												className="bg-secondary data-[state=on]:bg-primary data-[state=on]:text-white w-14"
											>
												{day}
											</ToggleGroupItem>
										))}
									</ToggleGroup>
								</FormItem>
							)}
						/>
					</section>
					<div className="space-y-4">
						<div className="flex items-center space-x-2">
							<FormField
								control={form.control}
								name="considerHolidays"
								render={({ field }) => (
									<>
										<Checkbox
											id="holidays"
											checked={field.value}
											onCheckedChange={(c) => field.onChange(c)}
										/>
										<Label htmlFor="holidays" className="font-medium">
											Include US national holidays
										</Label>
									</>
								)}
							/>
						</div>

						{form.getValues().considerHolidays && (
							<Card className="mt-4">
								<CardContent className="grid grid-cols-2 gap-3 gap-x-8 p-6">
									{holidays.isLoading && <Small>Loading...</Small>}
									{!holidays.isLoading && !holidays.data?.length && (
										<Small>No holidays in selected date range</Small>
									)}
									{holidays.data?.map((holiday) => (
										<div
											key={`${holiday.localName}-${holiday.date}`}
											className="flex justify-between items-center"
										>
											<Small className="text-muted-foreground">
												{holiday.localName}
											</Small>
											<Small className="text-foreground">
												{holiday.date.formatShortMonthDay()}
											</Small>
										</div>
									))}
								</CardContent>
							</Card>
						)}
					</div>
				</div>
			</SettingsLayout>
		</RHFProvider>
	)
}
